/**
 * Supabase User Data Service
 * Replaces localStorage with Supabase-based data storage
 */

import { supabase } from './supabase'

export const userDataService = {
  /**
   * Get user profile data from Supabase (replaces localStorage.getItem('userProfile'))
   */
  async getUserProfile(userId) {
    if (!userId) return null
    
    // Use current user session data first (most reliable)
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (user && user.id === userId) {
        return {
          personalInfo: user.user_metadata || {},
          fullName: user.user_metadata?.full_name || user.email?.split('@')[0] || 'User',
          email: user.email,
          role: user.user_metadata?.role || 'user',
          isAdmin: user.user_metadata?.is_admin || user.user_metadata?.role === 'super_admin',
          status: user.user_metadata?.status || 'approved',
          createdAt: user.created_at,
          lastSignIn: user.last_sign_in_at
        }
      }
    } catch (e) {
      console.debug('Current user session not available')
    }
    
    // No fallback to user_profiles table - using auth metadata only
    console.debug('User data retrieved from auth metadata only')
    return null
  },

  /**
   * Save user profile data using auth metadata only (no table dependencies)
   */
  async saveUserProfile(userId, profileData) {
    if (!userId) return false
    
    try {
      console.log('Saving user profile to auth metadata only...')
      
      // Update auth metadata directly
      const { error } = await supabase.auth.updateUser({
        data: {
          ...profileData.personalInfo,
          full_name: profileData.fullName,
          role: profileData.role || 'user',
          status: profileData.status || 'approved'
        }
      })
      
      if (!error) {
        console.log('✅ User profile saved to auth metadata')
        return true
      } else {
        console.error('❌ Error saving to auth metadata:', error)
        return false
      }
    } catch (e) {
      console.error('Error saving user profile:', e)
      return false
    }
  },

  /**
   * Get customization data from localStorage only (no table dependencies)
   */
  async getCustomizationData(userId) {
    if (!userId) return { personalInfo: {}, jobInfo: {}, preferences: {} }
    
    console.log('Getting customization data from localStorage only...')
    
    // Get from localStorage as primary source
    try {
      const customizationData = localStorage.getItem('interview_customization')
      if (customizationData) {
        const parsed = JSON.parse(customizationData)
        console.log('✅ Found customization data in localStorage')
        return {
          personalInfo: parsed.personalInfo || {},
          jobInfo: parsed.jobInfo || {},
          preferences: parsed.preferences || {}
        }
      }
    } catch (e) {
      console.debug('Error parsing customization data from localStorage')
    }
    
    // Fallback to user profile data
    const profile = await this.getUserProfile(userId)
    return {
      personalInfo: profile?.personalInfo || {},
      jobInfo: {},
      preferences: {}
    }
  },

  /**
   * Save customization data to localStorage only (no table dependencies)
   */
  async saveCustomizationData(userId, customizationData) {
    if (!userId) return false
    
    try {
      console.log('Saving customization data to localStorage only...')
      
      // Save to localStorage as primary storage
      localStorage.setItem('interview_customization', JSON.stringify(customizationData))
      console.log('✅ Customization data saved to localStorage')
      
      // Also update auth metadata with personal info if available
      if (customizationData.personalInfo) {
        await this.saveUserProfile(userId, {
          personalInfo: customizationData.personalInfo,
          fullName: customizationData.personalInfo?.fullName,
          email: customizationData.personalInfo?.email
        })
      }
      
      return true
    } catch (e) {
      console.error('Error saving customization data:', e)
      return false
    }
  },

  /**
   * Get system settings from Supabase (replaces localStorage config)
   */
  async getSystemSetting(userId, settingKey, defaultValue = null) {
    if (!userId) return defaultValue
    
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('setting_value')
        .eq('user_id', userId)
        .eq('setting_key', settingKey)
        .single()
      
      if (!error && data) {
        return data.setting_value || defaultValue
      }
    } catch (e) {
      console.log('system_settings table not available')
    }
    
    return defaultValue
  },

  /**
   * Save system setting to Supabase (replaces localStorage config)
   */
  async saveSystemSetting(userId, settingKey, settingValue) {
    if (!userId) return false
    
    try {
      const { error } = await supabase
        .from('system_settings')
        .upsert([
          {
            user_id: userId,
            setting_key: settingKey,
            setting_value: settingValue
          }
        ])
      
      return !error
    } catch (e) {
      console.log('system_settings table not available')
      return false
    }
  },

  /**
   * Clear all user data (localStorage only - no database calls)
   */
  async clearUserData(userId) {
    if (!userId) return
    
    // Only clear localStorage data - no database table dependencies
    console.log('Clearing localStorage data only...')
    
    // Auth metadata will be cleared by supabase.auth.signOut()
    // No need to delete from missing tables
  }
}

export default userDataService
